import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import SigniaBadge from "@/components/SigniaBadge";

// All hero imagery is served exclusively from the official Signia CDN
// (cdn.signia-pro.com). No stock, no proxy — Signia sources only.
const HERO_LIFESTYLE = "https://cdn.signia-pro.com/-/media/signia/global/images/campaigns/signia-ix/styletto-ix/styletto-ix_woman-holding-hearing-aid_1920x1080_v2.jpg";
const HERO_PRODUCT = "https://cdn.signia-pro.com/-/media/signia/global/images/products/signia-ix/styletto-ix/styletto-ix_black-silver_double_charger_1920x1080.jpg";
const HERO_LIFESTYLE_ALT = "https://cdn.signia-pro.com/-/media/signia/global/images/campaigns/signia-ix/styletto-ix/styletto-ix_woman-holding-hearing-aid_1920x1080.jpg";
const HERO_PRODUCT_ALT = "https://cdn.signia-pro.com/-/media/signia/global/images/products/signia-ix/styletto-ix/styletto-ix_black_silver_double_1000x1000.jpg";

export default function Hero() {
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446" });
    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    return (
        <section className="relative pt-12 md:pt-20 pb-10 md:pb-24 overflow-hidden" data-testid="hero-section">
            <div className="container-x">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* Left copy */}
                    <div className="lg:col-span-6 reveal">
                        <div data-testid="hero-signia-badge">
                            <SigniaBadge variant="ribbon" />
                        </div>
                        <span className="eyebrow inline-flex items-center gap-2 mt-5">
                            <Sparkles size={12} /> Powered by HearClear
                        </span>
                        <h1 className="font-serif text-[44px] md:text-[64px] xl:text-[76px] leading-[1.02] mt-5 text-[#1a1a1a]">
                            Signia Styletto IX. <br />
                            <span className="italic text-[#003E4D]">Slim. Smart. Powerful.</span>
                        </h1>
                        <p className="mt-6 text-[16px] md:text-[18px] text-[#4a5568] max-w-[520px] leading-relaxed">
                            Experience premium rechargeable hearing technology with expert guidance from
                            HearClear — a <b>Signia Certified Partner</b>. Ultra-slim design. AI-powered speech
                            clarity. All-day battery.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <WhatsAppCTA
                                waNumber={settings.whatsapp_number}
                                message="Hi, I want the best price for Signia Styletto IX."
                                sourcePage="hero"
                                className="btn-whatsapp"
                                data-testid="hero-whatsapp-cta"
                            >
                                <MessageCircle size={16} /> Get Best Price on WhatsApp
                            </WhatsAppCTA>
                            <Link to="/products" className="btn-primary" data-testid="hero-shop-cta">
                                Buy Online <ArrowRight size={16} />
                            </Link>
                            <Link to="/book-trial" className="btn-secondary" data-testid="hero-trial-cta">
                                Book Free Trial
                            </Link>
                        </div>

                        <div className="mt-10 grid grid-cols-3 gap-4 max-w-[460px]">
                            <Stat label="Warranty" value="Up to 4 yr" />
                            <Stat label="Trial" value="3-day" />
                            <Stat label="EMI from" value="₹12.5K/mo" />
                        </div>
                    </div>

                    {/* Right visual — asymmetric cluster */}
                    <div className="lg:col-span-6 relative reveal reveal-delay-1">
                        <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden bg-[#f3f4f6]">
                            <img
                                src={HERO_LIFESTYLE}
                                alt="Woman holding Signia Styletto IX"
                                className="w-full h-full object-cover"
                                loading="eager"
                                data-testid="hero-lifestyle-image"
                                onError={(e) => { if (!e.currentTarget.dataset.alt) { e.currentTarget.dataset.alt = "1"; e.currentTarget.src = HERO_LIFESTYLE_ALT; } }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-transparent" />
                        </div>
                        <div className="absolute -bottom-8 -left-4 md:-left-12 w-[64%] md:w-[58%] aspect-[4/3] rounded-[24px] overflow-hidden bg-white border border-black/5 shadow-[0_30px_60px_-22px_rgba(0,0,0,0.25)]">
                            <img
                                src={HERO_PRODUCT}
                                alt="Signia Styletto IX with charging case"
                                className="w-full h-full object-cover"
                                data-testid="hero-product-image"
                                onError={(e) => { if (!e.currentTarget.dataset.alt) { e.currentTarget.dataset.alt = "1"; e.currentTarget.src = HERO_PRODUCT_ALT; } }}
                            />
                        </div>
                        <div className="hidden md:flex absolute -top-4 -right-4 bg-white px-5 py-3 rounded-full shadow-[0_14px_30px_-12px_rgba(0,0,0,0.15)] border border-black/5 items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
                            <span className="text-[12px] font-semibold tracking-wide">Audiologists online now</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <p className="font-serif text-[28px] text-[#003E4D] leading-none">{value}</p>
            <p className="text-[12px] text-[#9CA3AF] uppercase tracking-[0.18em] mt-1">{label}</p>
        </div>
    );
}
